import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TrendingUp, MousePointerClick, Eye, Percent, Gauge } from "lucide-react";

const RANGES = [7, 28, 90];

export default function SearchPerfCard() {
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api
      .get("/admin/search-performance", { params: { days } })
      .then((r) => {
        setData(r.data);
        setState("ok");
      })
      .catch((e) => setState(e?.response?.status === 503 ? "setup" : "error"));
  }, [days]);

  const t = data?.totals;
  const metrics = t
    ? [
        { icon: MousePointerClick, label: "Clicks", value: t.clicks.toLocaleString(), testid: "search-perf-clicks" },
        { icon: Eye, label: "Impressions", value: t.impressions.toLocaleString(), testid: "search-perf-impressions" },
        { icon: Percent, label: "CTR", value: `${(t.ctr * 100).toFixed(2)}%`, testid: "search-perf-ctr" },
        { icon: Gauge, label: "Avg. position", value: t.position.toFixed(1), testid: "search-perf-position" },
      ]
    : [];
  const maxImp = Math.max(...(data?.rows || []).map((r) => r.impressions), 1);

  return (
    <div data-testid="search-perf-card" className="mt-6 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
          <TrendingUp size={14} className="text-[#C71E2E]" /> Google Search Performance
        </p>
        {state === "ok" && (
          <div className="flex gap-1.5">
            {RANGES.map((d) => (
              <button
                key={d}
                data-testid={`search-perf-range-${d}`}
                onClick={() => setDays(d)}
                className={`rounded-full px-3.5 py-1 text-xs font-ui transition-colors duration-200 ${days === d ? "bg-[#C71E2E] text-white" : "bg-[#F5EFE6] text-[#4A4A4A] hover:bg-[#E5DED5]"}`}
              >
                {d}d
              </button>
            ))}
          </div>
        )}
      </div>

      {state === "loading" && <p className="mt-4 text-sm font-ui text-[#4A4A4A]">Loading search data…</p>}

      {state === "setup" && (
        <div data-testid="search-perf-setup" className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
          <p className="text-sm font-ui font-semibold text-amber-800">Connect Google Search Console</p>
          <ol className="mt-2 text-xs font-ui text-[#4A4A4A] leading-relaxed list-decimal list-inside space-y-1">
            <li>Verify the site first — paste your code in the <Link to="/admin?tab=seo" className="text-[#C71E2E] underline underline-offset-2">SEO &amp; Verification</Link> tab and confirm at search.google.com/search-console.</li>
            <li>In Google Cloud: create a service account, enable the Search Console API, download the JSON key, then add the service-account email as a user on your Search Console property.</li>
            <li>Add the key (base64) to the server .env as GSC_SERVICE_ACCOUNT_JSON_B64 — this card lights up automatically.</li>
          </ol>
        </div>
      )}

      {state === "error" && (
        <p data-testid="search-perf-error" className="mt-4 text-sm font-ui text-[#C71E2E]">
          Couldn't load Search Console data — check the service-account key and that it's added to the property.
        </p>
      )}

      {state === "ok" && t && (
        <>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {metrics.map(({ icon: Icon, label, value, testid }) => (
              <div key={testid} data-testid={testid} className="rounded-xl border border-[#E5DED5] p-4">
                <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-[#4A4A4A] flex items-center gap-1.5">
                  <Icon size={12} className="text-[#C71E2E]" /> {label}
                </p>
                <p className="mt-1.5 font-ui text-2xl font-semibold text-[#1A1A1A]">{value}</p>
              </div>
            ))}
          </div>
          <p data-testid="search-perf-range-label" className="mt-3 text-xs font-ui text-[#4A4A4A]">
            {data.start_date} → {data.end_date} · {data.site}
          </p>
          {data.rows.length > 0 ? (
            <div data-testid="search-perf-bars" className="mt-4 flex items-end gap-[3px] h-24">
              {data.rows.map((r) => (
                <div
                  key={r.date}
                  data-testid={`search-perf-bar-${r.date}`}
                  className={`flex-1 rounded-t-sm transition-all duration-300 ${r.impressions > 0 ? "bg-[#C71E2E]/70 hover:bg-[#C71E2E]" : "bg-[#E5DED5]"}`}
                  style={{ height: `${Math.max((r.impressions / maxImp) * 100, 3)}%` }}
                  title={`${r.date}: ${r.clicks} clicks · ${r.impressions} impressions`}
                />
              ))}
            </div>
          ) : (
            <p data-testid="search-perf-empty" className="mt-4 text-xs font-ui text-[#4A4A4A]">
              No search data yet — Google starts reporting a few days after the site is verified and indexed.
            </p>
          )}
        </>
      )}
    </div>
  );
}
